import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { refreshToken } from '../modules/auth/redux/AuthCRUD'

// handlers (único mecanismo)
let onLogout: (() => void) | null = null
let getAuthState: (() => any) | null = null
let onTokenRefresh: ((access: string, refresh: string) => void) | null = null

export const injectAuthHandlers = (handlers: {
  onLogout: () => void
  getAuthState: () => any
  onTokenRefresh: (access: string, refresh: string) => void
}) => {
  onLogout = handlers.onLogout
  getAuthState = handlers.getAuthState
  onTokenRefresh = handlers.onTokenRefresh
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://lf3566q8-8000.usw3.devtunnels.ms/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // Send/receive the HttpOnly refresh cookie on cross-origin requests. The
  // backend scopes the cookie to /api/v1/token/ so it never travels on other
  // endpoints, but withCredentials still has to be on for the browser to
  // include it at all.
  withCredentials: true,
})

// control de refresh concurrente
let isRefreshing = false
let failedQueue: {
  resolve: (value?: unknown) => void
  reject: (reason?: any) => void
}[] = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(token)
  })
  failedQueue = []
}

// REQUEST interceptor
apiClient.interceptors.request.use((config) => {
  const state = getAuthState?.()
  const token = state?.auth?.accessToken

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    }
  }

  return config
})

// RESPONSE interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      return Promise.reject(error)
    }

    interface CustomAxiosRequestConfig extends AxiosRequestConfig {
      _retry?: boolean
    }

    const originalRequest = error.config as CustomAxiosRequestConfig

    // evitar loop en refresh
    if (originalRequest?.url?.includes('/token/refresh/')) {
      onLogout?.()
      return Promise.reject(error)
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${token}`,
          }
          return apiClient(originalRequest)
        })
      }

      isRefreshing = true

      try {
        // Refresh token lives in an HttpOnly cookie set by the backend; we no
        // longer read it from Redux/localStorage. If the cookie is missing or
        // expired the backend replies 401 and we log the user out below.
        const res = await refreshToken()
        const newAccess = res.access

        // actualizar redux vía handler (refresh se mantiene server-side)
        onTokenRefresh?.(newAccess, '')

        apiClient.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${newAccess}`

        processQueue(null, newAccess)

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccess}`,
        }

        return apiClient(originalRequest)
      } catch (err) {
        processQueue(err, null)
        onLogout?.()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient